import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { useResponsive } from 'src/hooks/use-responsive';
import Iconify from 'src/components/iconify';
import { LoSam } from 'src/types/losam';
import { OptionModel } from 'src/types/api-response';
import { useLoSam } from 'src/sections/losam/hooks/use-losam';
import { useAuthContext } from 'src/auth/context/jwt';
import GinsengCellInfo from './components/GinsengCellInfo';
import { LoSamPosition } from './hooks/use-tracu';
import useCaySamFilterOptions from '../../utils/use-caysam-filter-options';
import CaySamEditDrawer from './CaySamEditDrawer';
import CaySamNewDrawer from './CaySamNewDrawer';

type Props = {
  lotName: string;
  selectedLot: LoSam | null;
  gridPositions: LoSamPosition[][];
  gridSize: { x: number; y: number };
  caySamsLoading: boolean;
  onCellClick: (x: number, y: number) => void;
  getGinsengInfo: (x: number, y: number) => any;
  highlightStatus: number;
  highlightHealth: number;
  highlightAge: number;
  excludeLoggedThisMonth?: boolean;
  onDeselectGinseng?: (x: number, y: number) => void;
  deselectedGinsengs?: Set<string>;
  onOpenDrawer?: (caySamData: any, position: { x: number; y: number }) => void;
  loaiTuoiOptions: OptionModel[];
  diemSucKhoeOptions?: OptionModel[];
  onUpdateCaySam?: (caySamData: Partial<any>) => void;
  onSaveCaySam?: () => void;
  onDeleteCaySam?: () => void;
  onCaySamUpdated?: () => void; // **THÊM CALLBACK MỚI**
};

export default function LotGrid({ 
  lotName, 
  selectedLot, 
  gridPositions, 
  gridSize, 
  caySamsLoading, 
  onCellClick, 
  getGinsengInfo, 
  highlightStatus, 
  highlightHealth, 
  highlightAge, 
  excludeLoggedThisMonth = true, 
  onDeselectGinseng, 
  deselectedGinsengs, 
  onOpenDrawer, 
  loaiTuoiOptions, 
  diemSucKhoeOptions, 
  onUpdateCaySam, 
  onSaveCaySam, 
  onDeleteCaySam,
  onCaySamUpdated // **NHẬN CALLBACK MỚI**
}: Props) {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');
  const { tinhTrangOptions, tuoicayOptions, getTuoiText, getTrangThaiText } = useCaySamFilterOptions();
  const { GetCaySamByUser } = useLoSam();
  
  // State for drawer management
  const [editDrawerOpen, setEditDrawerOpen] = React.useState(false);
  const [newDrawerOpen, setNewDrawerOpen] = React.useState(false);
  const [selectedCaySam, setSelectedCaySam] = React.useState<any>(null);
  const [selectedPosition, setSelectedPosition] = React.useState<{ x: number; y: number } | null>(null);
  const [caySamData, setCaySamData] = React.useState<any>(null);
  const { isAdmin } = useAuthContext();

  if (gridPositions.length === 0) return null;
  let isOwnedByUser = true;

  const middleColumn = Math.floor(gridSize.x / 2);
  const getColumnLabel = (x: number) => {
    if (x === middleColumn) return '';
    const adjustedX = x > middleColumn ? x - 1 : x;
    return String.fromCharCode(65 + adjustedX);
  };
  const getRowLabel = (y: number) => (y + 1).toString();

  // Helper functions for drawer management
  const handleOpenEditDrawer = (caySam: any, position: { x: number; y: number }) => {
    setSelectedCaySam(caySam);
    setSelectedPosition(position);
    setCaySamData(caySam);
    setEditDrawerOpen(true);
  };

  // **SỬA HÀM handleRefreshFromDrawer ĐỂ GỌI CALLBACK PARENT**
  const handleRefreshFromDrawer = (newData: any) => {
    if (!selectedPosition) return;
    
    // Đóng drawer
    handleCloseEditDrawer();
    
    // Gọi callback để parent reload toàn bộ dữ liệu
    if (onCaySamUpdated) {
      onCaySamUpdated();
    }
  };

  const handleOpenNewDrawer = (position: { x: number; y: number }) => {
    setSelectedPosition(position);
    const newCaySamData = {
      loSam_ID: selectedLot?.loSam_ID || 0,
      viTriTrongLo: `${getColumnLabel(position.x)}${getRowLabel(position.y)}`,
      blockChain: null,
      maCaySam: '',
      tuoiCay_ID: 1,
      caySamNhatKys: []
    };
    setCaySamData(newCaySamData);
    setNewDrawerOpen(true);
  };

  const handleCloseEditDrawer = () => {
    setEditDrawerOpen(false);
    setSelectedCaySam(null);
    setSelectedPosition(null);
    setCaySamData(null);
  };

  const handleCloseNewDrawer = () => {
    setNewDrawerOpen(false);
    setSelectedPosition(null);
    setCaySamData(null);
  };

  const handleUpdateCaySam = (updatedData: Partial<any>) => {
    setCaySamData((prev: any) => ({ ...prev, ...updatedData }));
    if (onUpdateCaySam) {
      onUpdateCaySam(updatedData);
    }
  };

  const handleSaveNewCaySam = () => {
    if (onSaveCaySam) {
      onSaveCaySam();
    }
    handleCloseNewDrawer();
  };

  const getStatusColorBackground = (status: number) => {
    switch (status) {
      case 1:
        return theme.palette.success.lighter; // Sống
      case 2:
        return theme.palette.blue[200]; // Ngủ đông
      case 3:
        return theme.palette.error.lighter; // Chết 
      default:
        return theme.palette.grey[50];
    }
  }

  return (
    <Paper sx={{ p: 0 }}>
      <Typography variant="h6" sx={{ mb: { xs: 1, md: 3 }, fontSize: { xs: '1rem', md: '1.25rem' } }}>
        Bản đồ lô: {lotName} ({selectedLot ? `${selectedLot.soCot} x ${selectedLot.soHang}` : `${gridSize.x - 1} x ${gridSize.y}`})
        {caySamsLoading && (
          <CircularProgress size={20} sx={{ ml: 2 }} />
        )}
      </Typography>

      <Box sx={{ display: 'flex', mb: { xs: 0.5, md: 1 }, ml: { xs: 3, md: 6 } }}>
        {Array.from({ length: gridSize.x }, (_, x) => (
          <Box key={x} sx={{
            width: { xs: 48, md: 120 },
            height: { xs: 18, md: 30 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: { xs: '9px', md: '14px' },
            fontWeight: 'bold',
            color: x === middleColumn ? 'transparent' : 'text.primary'
          }}>
            {getColumnLabel(x)}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.25, md: 1 } }}>
        {gridPositions.map((row, rowIndex) => (
          <Box key={rowIndex} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 1 } }}>
            <Box sx={{
              width: { xs: 18, md: 30 },
              height: { xs: 48, md: 120 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: { xs: '9px', md: '14px' },
              fontWeight: 'bold',
              color: 'text.primary'
            }}>
              {getRowLabel(rowIndex)}
            </Box>

            {row.map((position) => {
              const isWalkway = position.x === middleColumn;
              const ginsengInfo = getGinsengInfo(position.x, position.y);
              const filterActive = Boolean(highlightStatus || highlightHealth || highlightAge);
              const positionKey = `${position.x}-${position.y}`;
              const isDeselected = deselectedGinsengs?.has(positionKey) || false;
              
              const hasLogThisMonth = (() => {
                if (!position.caySam?.caySamNhatKys || position.caySam.caySamNhatKys.length === 0) return false;
                const now = new Date();
                return position.caySam.caySamNhatKys.some((nk: any) => {
                  const d = new Date(nk?.ngayGhi as any);
                  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                });
              })();

              const isHighlightMatch = !!ginsengInfo && !isDeselected && (
                (highlightStatus ? ginsengInfo.tinhTrang === highlightStatus : true) &&
                (highlightHealth ? ginsengInfo.diemSucKhoe === highlightHealth : true) &&
                (highlightAge ? String(ginsengInfo.tuoiCay_ID) === String(highlightAge) : true) &&
                !(excludeLoggedThisMonth && hasLogThisMonth)
              );

              const getCellColors = () => {
                if (isWalkway) {
                  return { borderColor: 'transparent', backgroundColor: 'transparent', textColor: 'transparent', hoverColor: 'transparent' };
                }

                if (filterActive) {
                  if (isDeselected) {
                    return { borderColor: 'grey.300', backgroundColor: 'grey.50', textColor: 'text.secondary', hoverColor: 'grey.200' };
                  }
                  if (isHighlightMatch) { 
                    return { borderColor: 'error.main', backgroundColor: 'error.lighter', textColor: 'error.dark', hoverColor: 'error.lighter' };
                  }
                  return { borderColor: 'grey.300', backgroundColor: 'grey.100', textColor: 'grey.500', hoverColor: 'grey.200' };
                } 
 
                if (position.selected) {
                  return { borderColor: 'primary.main', backgroundColor: 'primary.lighter', textColor: 'primary.main', hoverColor: 'primary.main' };
                }
                return { borderColor: 'grey.300', backgroundColor: 'grey.50', textColor: 'text.secondary', hoverColor: 'grey.200' };
              };

              const colors = getCellColors();

              const tooltipHeaderText = (() => {
                if (filterActive) {
                  if (isDeselected) return 'Cây sâm - Nhấn để chọn lại';
                  if (isHighlightMatch) return 'Cây sâm - Nhấn để bỏ chọn khỏi kết quả lọc';
                  return 'Thông tin cây sâm';
                }
                return 'Thông tin cây sâm';
              })();

              const lastTinhTrang = position.caySam?.caySamNhatKys && position.caySam.caySamNhatKys.length > 0
                ? position.caySam.caySamNhatKys[position.caySam.caySamNhatKys.length - 1]?.tinhTrang
                : 0;
              
              if (!isAdmin) {
                isOwnedByUser = GetCaySamByUser.some(item => item.caySamId === position.caySam?.caySam_ID) || false;
              }

              const handleCellClick = () => {
                if (isWalkway) return;
                const canToggleDeselect = filterActive && !!ginsengInfo && onDeselectGinseng;
                if (canToggleDeselect) {
                  onDeselectGinseng(position.x, position.y);
                  return;
                }
                if (position.occupied && position.caySam) {
                  handleOpenEditDrawer(position.caySam, { x: position.x, y: position.y });
                  return;
                }
                if (!position.occupied) {
                  handleOpenNewDrawer({ x: position.x, y: position.y });
                  return;
                }

                onCellClick(position.x, position.y);
              };

              const hasLogThisMonthForIcon = (() => {
                const list = position.caySam?.caySamNhatKys || [];
                if (!list || list.length === 0) return false;
                const now = new Date();
                return list.some((nk: any) => {
                  const d = new Date(nk?.ngayGhi as any);
                  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                });
              })();

              return (
                <Box
                  key={`${position.x}-${position.y}`}
                  onClick={isAdmin || isOwnedByUser ? handleCellClick : undefined}
                  sx={{
                    width: { xs: 48, md: 120 },
                    height: { xs: 48, md: 120 },
                    border: '2px solid', 
                    borderColor: colors.borderColor,
                    backgroundColor: isAdmin || isOwnedByUser ? getStatusColorBackground(position.occupied ? lastTinhTrang ?? 0 : 0) : theme.palette.grey[200],
                    cursor: isWalkway || (position.occupied && !isOwnedByUser) ? 'default' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '7px', md: '11px' },
                    fontWeight: 'bold',
                    color: (position.occupied && isOwnedByUser) ? theme.palette.text.primary : theme.palette.text.disabled,
                    '&:hover': { backgroundColor: (position.occupied && isOwnedByUser) ? theme.palette.success.light : colors.hoverColor, transform: isMobile ? 'scale(1.03)' : 'scale(1.02)' },
                    transition: 'all 0.2s',
                    position: 'relative',
                    borderRadius: 1,
                  }}
                >
                  {hasLogThisMonthForIcon && (
                    <Iconify icon="solar:check-circle-bold" width={12} sx={{ position: 'absolute', top: 4, right: 4, color: 'success.main' }} />
                  )}
                  {position.caySam?.isNFC && (
                    <Iconify icon="mingcute:nfc-fill" width={12} sx={{ position: 'absolute', top: hasLogThisMonthForIcon ? 18 : 4, right: 4, color: 'warning.main' }} />
                  )}
                  {(() => {
                    if (isWalkway) {
                      return (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '65%', md: '100%' },
                            height: { xs: '2px', md: '3px' },
                            backgroundColor: 'grey.400',
                            borderRadius: '2px',
                          }}
                        />
                      );
                    }
                    if (position.occupied && ginsengInfo) {
                      return (
                        <Tooltip title={<Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>{tooltipHeaderText}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Vị trí: {getColumnLabel(position.x)}{getRowLabel(position.y)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Vị trí trong lô: {position.caySam?.viTriTrongLo}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Tuổi cây: {getTuoiText(position.caySam?.tuoiCay_ID)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Tình trạng: {getTrangThaiText(lastTinhTrang)}</Typography>
                          {filterActive && isHighlightMatch && !isDeselected && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main', fontWeight: 'bold' }}>
                              ⚠️ Đang được lọc - Nhấn để bỏ chọn
                            </Typography>
                          )}
                        </Box>} arrow placement="top">
                          <Box sx={{ textAlign: 'center', p: { xs: 0.25, md: 0.5 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 0.25, md: 0.5 } }}>
                              <GinsengCellInfo
                                positionLabel={`${getColumnLabel(position.x)}${getRowLabel(position.y)}`}
                                caySam={position.caySam || null}
                                latestNhatKy={position.caySam?.caySamNhatKys && position.caySam.caySamNhatKys.length > 0 
                                  ? position.caySam.caySamNhatKys[position.caySam.caySamNhatKys.length - 1] as any
                                  : null}
                                tuoiOptions={tuoicayOptions} 
                                tinhTrangOptions={tinhTrangOptions}
                                size="xs"
                                center
                              />
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip title={<Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Vị trí trống - Nhấn để thêm cây sâm</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Vị trí: {getColumnLabel(position.x)}{getRowLabel(position.y)}</Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>Trạng thái: Có thể trồng cây sâm mới</Typography>
                      </Box>} arrow placement="top">
                        <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="caption" sx={{
                            fontWeight: 'bold',
                            display: 'block',
                            mb: { xs: 0.5, md: 1 },
                            fontSize: { xs: '8px', md: '10px' }
                          }}>
                            {getColumnLabel(position.x)}{getRowLabel(position.y)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Iconify icon="solar:add-circle-bold" width={{ xs: 12, md: 16 }} sx={{ color: 'grey.400' }} />
                          </Box>
                          <Typography variant="caption" sx={{
                            display: 'block',
                            mt: { xs: 0.25, md: 0.5 },
                            fontSize: { xs: '7px', md: '9px' }
                          }}>
                            Trống
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Edit Drawer for existing plants */}
      {selectedCaySam && selectedPosition && (
        <CaySamEditDrawer
          open={editDrawerOpen}
          onClose={handleCloseEditDrawer}
          title={`Cập nhật cây sâm - ${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          lotName={lotName}
          lotCode={selectedLot?.maLo || ''}
          gardenName={selectedLot?.vuonTrongs?.[0]?.tenVuonTrong || ''}
          positionLabel={`${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          caySamData={caySamData}
          onUpdateCaySam={handleUpdateCaySam}
          loaiTuoiOptions={loaiTuoiOptions}
          diemSucKhoeOptions={diemSucKhoeOptions}
          loading={caySamsLoading}
          onRefresh={handleRefreshFromDrawer} // **TRUYỀN CALLBACK**
        />
      )}

      {/* New Drawer for adding new plants */}
      {selectedPosition && !selectedCaySam && (
        <CaySamNewDrawer
          open={newDrawerOpen}
          onClose={handleCloseNewDrawer}
          title={`Thêm cây sâm mới - ${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          lotName={lotName}
          lotCode={selectedLot?.maLo || ''}
          gardenName={selectedLot?.vuonTrongs?.[0]?.tenVuonTrong || ''}
          positionLabel={`${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          caySamData={caySamData}
          onUpdateCaySam={handleUpdateCaySam}
          loaiTuoiOptions={loaiTuoiOptions}
          diemSucKhoeOptions={diemSucKhoeOptions}
          loading={caySamsLoading}
          onSave={handleSaveNewCaySam}
        />
      )}
    </Paper>
  );
}